<template>
  <div class="chart">
    <b-button
      v-visible="selectMode"
      size="sm"
      variant="primary"
      class="float-right mx-1"
      @click="emitChartClick"
    >
      Apply
    </b-button>
    <svg :id="'svg-chart-' + id"></svg>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { logError } from '@/utils/loggerUtils';
export default {
  name: 'Chart',
  props: {
    width: {
      type: Number,
      default: 1000,
    },
    height: {
      type: Number,
      default: 800,
    },
    marginX: {
      type: Number,
      default: 40,
    },
    marginTop: {
      type: Number,
      default: 50,
    },
    marginBottom: {
      type: Number,
      default: 65,
    },
    chartId: {
      type: String,
      required: true,
    },
    chartTitle: {
      type: String,
      default: '',
    },
    chartXLabel: {
      type: String,
      default: '',
    },
    chartYLabel: {
      type: String,
      default: '',
    },
    // currently not used
    // this should be used for handling different y axis data types such as
    // 'float' or 'integer'
    chartYDataType: {
      type: String,
      default: 'float',
    },
    chartYDataPerc: {
      type: Boolean,
      default: true,
    },
    chartData: {
      type: Array,
      required: true,
    },
    chartDataSelected: {
      type: Array,
      required: true,
    },
    chartType: {
      type: String,
      default: 'non-timeline',
    },
    numTopOptions: {
      type: Number,
      default: 10,
    },
    fontSize: {
      type: String,
      default: '15px',
    },
  },
  data() {
    return {
      svg: null,
      chart: null,
      id: null,
      data: null,
      showClickMenu: false,
      selectMode: false,
    };
  },
  watch: {
    width: function () {
      if (this.chart !== null) {
        this.svg.selectAll('text').remove();
        this.chart.remove();
      }
      this.renderChart();
    },
  },
  mounted() {
    this.id = this.chartId;
    // if timeline, show most recent options
    if (this.chartType === 'timeline')
      this.data = this.chartData.slice(-1 * this.numTopOptions);
    // else show top options
    else this.data = this.chartData.slice(0, this.numTopOptions);
    this.dataSelected = [...this.chartDataSelected];
    this.title = this.chartTitle;

    this.xLabel = this.chartXLabel;
    this.yLabel = this.chartYLabel;
    this.yDataType = this.chartYDataType;
    this.yDataPerc = this.chartYDataPerc;
    this.selectedRefs = [];
    const dataPromise = new Promise((resolve) => {
      if (this.id && this.data) {
        resolve('Data Prepared');
      }
    });
    dataPromise
      .then(() => {
        this.renderChart();
      })
      .catch((error) => {
        logError(error);
      });
  },
  methods: {
    emitChartClick() {
      const self = this;
      this.selectMode = false;
      this.$emit('chart-click', this.dataSelected);
      d3.selectAll('rect').style('fill', (g) =>
        d3.color(self.dataSelected.includes(g.key) ? '#f05825' : 'steelblue')
      );
    },
    renderChart() {
      const maxCount = Math.max(...this.data.map((d) => d.doc_count));
      const xLabel = this.data.map((d) => {
        return d.key;
      });
      const self = this;
      const chartWidth = this.width - 2 * this.marginX;
      const chartHeight = this.height - this.marginTop - this.marginBottom;
      this.svg = d3
        .select('#svg-chart-' + this.id)
        .attr('width', this.width)
        .attr('height', this.height);
      // clear previous contents (if any)
      this.svg.selectAll('*').remove();
      this.chart = this.svg
        .append('g')
        .attr(
          'transform',
          `translate(${(3 * this.marginX) / 2}, ${this.marginTop})`
        );
      const xScale = d3
        .scaleBand()
        .range([0, chartWidth])
        .domain(xLabel)
        .padding(0.3);
      this.chart
        .append('g')
        .attr('transform', `translate(0, ${chartHeight})`)
        .call(
          d3.axisBottom(xScale).tickFormat(function (d) {
            return d.length > 10 ? d.slice(0, 9) + '..' : d;
          })
        )
        .selectAll('text')
        .attr('text-anchor', 'end')
        .attr('dx', '-.8em')
        .attr('dy', '.15em')
        .attr('transform', 'rotate(-45)');
      const yScale = d3
        .scaleLinear()
        .range([chartHeight, 0])
        .domain([0, maxCount]);
      this.chart.append('g').call(d3.axisLeft(yScale).ticks(10));
      const barGroups = this.chart.selectAll('rect').data(this.data).enter();
      barGroups
        .append('rect')
        .attr('class', 'bar')
        .style('fill', (g) =>
          d3.color(this.dataSelected.includes(g.key) ? '#f05825' : 'steelblue')
        )
        .attr('x', (g, i) => xScale(xLabel[i]))
        .attr('y', (g) => yScale(g.doc_count))
        .attr('height', (g) => chartHeight - yScale(g.doc_count))
        .attr('width', xScale.bandwidth())
        .on('mouseenter', function (event, curr) {
          d3.select(this)
            .transition()
            .duration(500)
            .attr('opacity', 0.6)
            .style('cursor', 'pointer')
            .attr('x', (a) => xScale(a.key) - xScale.bandwidth() / 20)
            .attr('width', xScale.bandwidth() + xScale.bandwidth() / 10);
          self.svg
            .append('text')
            .attr('class', 'value')
            .attr('x', self.width / 2)
            .attr('y', 40)
            .attr('text-anchor', 'middle')
            .style('fill', d3.color('steelblue'))
            .text(
              (curr.key.length > 35 ? curr.key.slice(0, 35) + '..' : curr.key) +
                ': ' +
                (self.yDataPerc
                  ? curr.doc_count.toFixed(2) + '%'
                  : curr.doc_count)
            );
        })
        .on('mouseleave', function () {
          d3.select(this)
            .transition()
            .duration(300)
            .attr('opacity', 1)
            .attr('x', (a) => xScale(a.key))
            .attr('width', xScale.bandwidth())
            .style('cursor', 'default');
          self.svg.selectAll('.value').remove();
        })
        .on('click', function (event, curr) {
          event.stopPropagation();
          if (!self.selectMode) {
            self.selectMode = true;
          }
          if (self.dataSelected.includes(curr.key)) {
            self.dataSelected.splice(self.dataSelected.indexOf(curr.key), 1);
            self.selectedRefs.splice(self.dataSelected.indexOf(curr.key), 1);
            d3.select(this).style('fill', 'steelblue');
          } else {
            // if chart type is 'timeline', then remove existing selection
            // because selection of only one year is allowed
            if (self.chartType === 'timeline') {
              self.dataSelected.splice(0, self.dataSelected.length);
              if (self.selectedRefs.length > 0) {
                d3.select(self.selectedRefs[0]).style('fill', 'steelblue');
                self.selectedRefs.splice(0, self.selectedRefs.length);
              }
            }
            self.dataSelected.push(curr.key);
            d3.select(this).style('fill', 'orange');
            // store the references of the selected bars (so that they can
            // be accessed later)
            self.selectedRefs.push(this);
          }
        });
      this.addLabels(chartWidth, chartHeight);
    },
    addLabels(width, height) {
      this.svg
        .append('text')
        .attr('class', 'label')
        .attr('x', -(height / 2) - this.marginX)
        .attr('y', this.marginTop / 2.4)
        .attr('transform', 'rotate(-90)')
        .attr('text-anchor', 'middle')
        .text(this.yLabel)
        .style('font-size', this.fontSize);
      this.svg
        .append('text')
        .attr('class', 'label')
        .attr('x', width / 2 + this.marginX)
        .attr(
          'y',
          height + this.marginTop + this.marginTop + this.marginBottom * 0.7
        )
        .attr('text-anchor', 'middle')
        .text(this.xLabel);
      this.svg
        .append('text')
        .attr('class', 'title')
        .attr('x', width / 2 + this.marginX)
        .attr('y', 15)
        .attr('text-anchor', 'middle')
        .text(this.title)
        .style('font-size', this.fontSize);
    },
  },
};
</script>

<style></style>
